/**
 * Phase 14.14 Deliverable — Sensitivity-driver 3D viewer ("the third 3D").
 *
 * Sibling to PkTrajectory3D (Phase 11) and GeometrySweep3D (Phase 14.13).
 * Reuses every shared visual primitive from `lib/viz3d.js`:
 *   - polarToCart (engagement-geometry placement)
 *   - pkColour (RdYlGn ramp)
 *   - buildSceneLayout (locked camera + axis defaults)
 *   - PLOT_BUTTONS_TO_REMOVE (mode bar)
 *   - classifyDriverAxis (spatial classification)
 *
 * Visual scene:
 *   - Origin marker at launch (0, 0, 0) — same primitive as the other
 *     viewers so the operator instantly recognises "this is an
 *     engagement frame".
 *   - One canonical engagement-geometry reference point at a nominal
 *     (range, bearing, altitude) so the scene has spatial scale (a
 *     sensitivity campaign runs at one nominal geometry; we don't
 *     have an envelope of points to plot, so a single reference is
 *     the closest analogue to GeometrySweep3D's "envelope cloud").
 *   - The "tornado glyph": one bar per top driver, length ∝ variance
 *     contribution, colour from the shared RdYlGn ramp:
 *       • spatial drivers (altitude / azimuth / range) anchor to the
 *         engagement-geometry axis they apply to
 *       • non-spatial drivers (sensor noise, fuel margin, propulsion
 *         efficiency, etc.) cluster in a side stack at fixed offset
 *         from the scene
 *   - 4-line legend chip row: total variance explained, top-3
 *     drivers (name + %), n_samples, dominant failure mode.
 *
 * The "engagement envelope" the PM brief asks for is, for a
 * sensitivity campaign, a single reference point — the nominal
 * geometry at which the variance dimensions were perturbed. That
 * single point + tornado glyphs anchored to it gives the operator
 * the same look-and-feel as GeometrySweep3D without forcing fake
 * envelope-of-many-points data.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
    pkColour,
    polarToCart,
    buildSceneLayout,
    PLOT_BUTTONS_TO_REMOVE,
    classifyDriverAxis,
} from "@/lib/viz3d";

let PlotlyImpl = null;
async function getPlotly() {
    if (PlotlyImpl) return PlotlyImpl;
    try {
        const mod = await import("plotly.js-dist-min");
        PlotlyImpl = mod.default || mod;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[SensitivityDriver3D] plotly load failed:", e);
        PlotlyImpl = null;
    }
    return PlotlyImpl;
}

// Reference engagement geometry — the engine's nominal scenario.
// Used only as visual scale anchor; can be overridden via prop.
const NOMINAL_REF = { range_m: 1000, bearing_deg: 30, altitude_m: 250 };

// ──────────────────────────────────────────────────────────────────
// Pure helpers (exported for unit testability).
// ──────────────────────────────────────────────────────────────────

/**
 * Format a `var_id` like `target.altitude_m` into a short
 * operator-readable label like `target altitude`. Drops the unit
 * suffix (`_m`, `_mps`, `_deg`) and the namespace dot.
 */
export function formatVarLabel(varId) {
    const id = String(varId ?? "");
    const parts = id.split(".");
    const tail = parts[parts.length - 1] || id;
    return tail
        .replace(/_(?:m|mps|deg|m_per_s|s|hz|n)$/i, "")
        .replace(/_/g, " ");
}

/**
 * Build the 4-line legend.
 *
 * `total_variance_pct` — sum of the top-3 driver indices, capped at
 *   100. First-order Sobol-style indices may not sum to 1 when there
 *   are higher-order interactions, so this is a "top-3 contribution"
 *   summary not a closed-form variance accounting.
 * `top3` — formatted "name (xx%)" strings.
 * `n_samples` — pulled from each driver's index-record n_samples or
 *   from `samples_per_variable * |variable_subset|` if absent.
 * `dominant_failure_mode` — passed in from caller (the PK detail
 *   page already has it; we don't recompute).
 */
export function buildSensitivityLegend(sens, fallbackDominant = "n/a") {
    if (!sens || !Array.isArray(sens.top_drivers) || sens.top_drivers.length === 0) {
        return {
            total_variance_pct: 0,
            top3: [],
            n_samples: 0,
            dominant_failure_mode: fallbackDominant,
        };
    }
    const top = sens.top_drivers.slice(0, 3);
    const total = top.reduce(
        (acc, d) => acc + Math.max(0, Math.min(1, Number(d.magnitude ?? d.index ?? 0))),
        0,
    );
    const indices = sens.indices || {};
    let nSamples = 0;
    const seen = new Set();
    for (const d of sens.top_drivers) {
        const id = d.var_id;
        if (seen.has(id)) continue;
        seen.add(id);
        nSamples += Number(indices[id]?.n_samples ?? 0);
    }
    if (!nSamples) {
        const subset = Array.isArray(sens.variable_subset) ? sens.variable_subset.length : 0;
        nSamples = (Number(sens.samples_per_variable ?? 0) || 0) * (subset || sens.top_drivers.length);
    }
    const top3Strings = top.map(
        (d) => `${formatVarLabel(d.var_id)} (${(Number(d.magnitude ?? d.index ?? 0) * 100).toFixed(0)}%)`,
    );
    return {
        total_variance_pct: Math.min(100, total * 100),
        top3: top3Strings,
        n_samples: nSamples,
        dominant_failure_mode: fallbackDominant,
    };
}

/**
 * Build the Plotly traces for the scene. Pure: takes sens.top_drivers
 * + a reference geometry, returns an array of trace objects.
 *
 * Spatial-driver glyph:
 *   altitude → vertical bar at origin from z=0 up to ref.altitude × magnitude
 *   azimuth  → horizontal arc segment at fixed radius (small fraction of
 *              engagement range), spanning ±(magnitude × 60°) around the
 *              reference bearing
 *   radial   → radial bar at z=0 from origin out to range × magnitude
 *
 * Non-spatial drivers stack horizontally at a fixed side offset (right
 * of the engagement scene), each bar's x-extent ∝ magnitude.
 */
export function buildSensitivityTraces(topDrivers, ref = NOMINAL_REF) {
    if (!Array.isArray(topDrivers) || topDrivers.length === 0) return [];

    // Reference target start (so the scene has visible scale).
    const refPos = polarToCart(ref.range_m, ref.bearing_deg, ref.altitude_m);
    const sceneScale = Math.max(ref.range_m, ref.altitude_m, 1);

    const traces = [];

    // Origin marker.
    traces.push({
        type: "scatter3d",
        mode: "markers",
        x: [0],
        y: [0],
        z: [0],
        marker: {
            size: 8,
            color: "#fde68a",
            symbol: "diamond",
            line: { color: "#facc15", width: 1.0 },
        },
        name: "Launch (0,0,0)",
        hovertemplate: "Launch (0,0,0)<extra></extra>",
    });

    // Reference target start.
    traces.push({
        type: "scatter3d",
        mode: "markers",
        x: [refPos.x],
        y: [refPos.y],
        z: [refPos.z],
        marker: {
            size: 6,
            color: "#cbd5e1",
            symbol: "circle",
            line: { color: "#0f172a", width: 0.5 },
        },
        name: "Nominal target start",
        hovertemplate:
            "Nominal target start<br>(E %{x:.0f} m, N %{y:.0f} m, Up %{z:.0f} m)<extra></extra>",
    });

    // Faint reference closing path (launch → nominal target).
    traces.push({
        type: "scatter3d",
        mode: "lines",
        x: [0, refPos.x],
        y: [0, refPos.y],
        z: [0, refPos.z],
        line: { color: "#475569", width: 1, dash: "dash" },
        opacity: 0.5,
        hoverinfo: "skip",
        showlegend: false,
    });

    // Side-stack y-coordinate cursor for non-spatial drivers.
    let nonSpatialY = -0.4 * sceneScale;
    const nonSpatialX = 1.15 * sceneScale; // right of the scene
    const nonSpatialZStart = 0;
    const nonSpatialDz = 0.12 * sceneScale; // vertical spacing

    topDrivers.forEach((d, idx) => {
        const mag = Math.max(0, Math.min(1, Number(d.magnitude ?? d.index ?? 0)));
        const colour = pkColour(mag);
        const axis = classifyDriverAxis(d.var_id);
        const label = `${formatVarLabel(d.var_id)} · ${(mag * 100).toFixed(0)}%`;

        if (axis === "altitude") {
            const z1 = ref.altitude_m * (0.5 + 0.7 * mag);
            traces.push({
                type: "scatter3d",
                mode: "lines+markers",
                x: [0, 0],
                y: [0, 0],
                z: [0, z1],
                line: { color: colour, width: 8 },
                marker: { size: [0, 6], color: colour },
                name: label,
                hovertemplate: `${label}<br>axis = altitude<extra></extra>`,
            });
        } else if (axis === "azimuth") {
            const r = 0.45 * sceneScale;
            const sweepDeg = 30 + 30 * mag;
            const npts = 24;
            const xs = [];
            const ys = [];
            const zs = [];
            const half = sweepDeg / 2;
            const cb = (ref.bearing_deg ?? 0);
            for (let i = 0; i <= npts; i += 1) {
                const t = i / npts;
                const bearing = cb - half + sweepDeg * t;
                const rad = (bearing * Math.PI) / 180;
                xs.push(r * Math.sin(rad));
                ys.push(r * Math.cos(rad));
                zs.push(0);
            }
            traces.push({
                type: "scatter3d",
                mode: "lines",
                x: xs,
                y: ys,
                z: zs,
                line: { color: colour, width: 8 },
                name: label,
                hovertemplate: `${label}<br>axis = azimuth<extra></extra>`,
            });
        } else if (axis === "radial") {
            const r1 = ref.range_m * (0.4 + 0.6 * mag);
            const rad = ((ref.bearing_deg ?? 0) * Math.PI) / 180;
            traces.push({
                type: "scatter3d",
                mode: "lines+markers",
                x: [0, r1 * Math.sin(rad)],
                y: [0, r1 * Math.cos(rad)],
                z: [0, 0],
                line: { color: colour, width: 8 },
                marker: { size: [0, 6], color: colour },
                name: label,
                hovertemplate: `${label}<br>axis = radial<extra></extra>`,
            });
        } else {
            // Non-spatial → side-stack horizontal bar.
            const z = nonSpatialZStart + nonSpatialDz * idx;
            const barLen = (0.35 + 0.65 * mag) * sceneScale;
            traces.push({
                type: "scatter3d",
                mode: "lines+markers",
                x: [nonSpatialX, nonSpatialX + barLen],
                y: [nonSpatialY, nonSpatialY],
                z: [z, z],
                line: { color: colour, width: 8 },
                marker: { size: [0, 6], color: colour },
                name: label,
                hovertemplate: `${label}<br>axis = non-spatial<extra></extra>`,
            });
        }
    });

    return traces;
}

// ──────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────
export default function SensitivityDriver3D({
    campaign,
    height = 480,
    referenceGeometry = NOMINAL_REF,
    "data-testid": dataTestId = "sensitivity-driver-tornado-3d",
}) {
    const containerRef = useRef(null);
    const [plotlyReady, setPlotlyReady] = useState(false);
    const [plotlyAvailable, setPlotlyAvailable] = useState(true);

    const sens = campaign?.sensitivity_results;
    const topDrivers = sens?.top_drivers || [];
    const dominantFailure = useMemo(() => {
        const counts = campaign?.failure_mode_counts || {};
        const entries = Object.entries(counts);
        if (!entries.length) return "n/a";
        return entries.sort((a, b) => b[1] - a[1])[0][0];
    }, [campaign?.failure_mode_counts]);
    const traces = useMemo(
        () => buildSensitivityTraces(topDrivers, referenceGeometry),
        [topDrivers, referenceGeometry],
    );
    const legend = useMemo(
        () => buildSensitivityLegend(sens, dominantFailure),
        [sens, dominantFailure],
    );

    useEffect(() => {
        let cancelled = false;
        getPlotly().then((mod) => {
            if (cancelled) return;
            if (mod) setPlotlyReady(true);
            else setPlotlyAvailable(false);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!plotlyReady) return;
        if (!containerRef.current) return;
        if (!traces.length) return;
        const layout = buildSceneLayout();
        getPlotly().then((mod) => {
            if (!mod) return;
            mod.react(containerRef.current, traces, layout, {
                responsive: true,
                displaylogo: false,
                modeBarButtonsToRemove: PLOT_BUTTONS_TO_REMOVE,
            });
        });
    }, [plotlyReady, traces]);

    if (!topDrivers.length) {
        return (
            <div
                className="rounded-md border border-dashed border-slate-700 bg-slate-900/40 p-6 text-sm text-muted-foreground text-center"
                data-testid={`${dataTestId}-empty`}
            >
                No sensitivity drivers to render — the campaign may not have completed yet.
            </div>
        );
    }
    if (!plotlyAvailable) {
        return (
            <div
                className="rounded-md border border-dashed border-amber-700/50 bg-amber-900/10 p-6 text-sm text-amber-200 text-center"
                data-testid={`${dataTestId}-unavailable`}
            >
                3D viewer unavailable in this build.
            </div>
        );
    }

    return (
        <div data-testid={dataTestId} className="space-y-3">
            <div className="rounded-md border border-slate-800 overflow-hidden">
                {plotlyReady ? (
                    <div ref={containerRef} style={{ width: "100%", height }} />
                ) : (
                    <Skeleton className="w-full" style={{ height }} />
                )}
            </div>
            <div className="flex flex-wrap gap-3 text-xs">
                <span
                    data-testid={`${dataTestId}-legend-total-variance`}
                    className="rounded bg-slate-800/60 px-2 py-1"
                >
                    total variance explained:{" "}
                    <strong>{legend.total_variance_pct.toFixed(0)}%</strong>
                </span>
                <span
                    data-testid={`${dataTestId}-legend-top-drivers`}
                    className="rounded bg-slate-800/60 px-2 py-1"
                >
                    top drivers:{" "}
                    <strong>
                        {legend.top3.length ? legend.top3.join(" · ") : "n/a"}
                    </strong>
                </span>
                <span
                    data-testid={`${dataTestId}-legend-n-samples`}
                    className="rounded bg-slate-800/60 px-2 py-1"
                >
                    n_samples: <strong>{legend.n_samples}</strong>
                </span>
                <span
                    data-testid={`${dataTestId}-legend-dominant`}
                    className="rounded bg-slate-800/60 px-2 py-1"
                >
                    dominant failure mode: <strong>{legend.dominant_failure_mode}</strong>
                </span>
            </div>
        </div>
    );
}
